function defaultEscapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function normalizeOperator(value) {
  return String(value ?? "").trim().toLowerCase() === "and" ? "and" : "or";
}

function normalizeNegated(value) {
  return value === true;
}

function normalizeQueryTextRelation(value) {
  const relation = String(value ?? "").trim();
  return ["current-text", "valid-prefix", "prior-query"].includes(relation)
    ? relation
    : "none";
}

const DEFAULT_MEANING_COPY = Object.freeze({
  appliedQuery: "Applied query meaning",
  appliedTerm: "Applied term",
  exclude: "Exclude",
  matchAll: "Match all",
  matchAny: "Match any",
  containerContaining: "Container containing",
  conjunctionAnd: "AND",
  conjunctionOr: "OR",
});

function normalizeMeaningCopy(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_MEANING_COPY).map(([key, fallback]) => {
      const copy = String(source[key] ?? "").trim();
      return [key, copy || fallback];
    }),
  );
}

function queryTextRelationAttribute(relation, escapeHtml) {
  return ` data-query-text-relation="${escapeHtml(normalizeQueryTextRelation(relation))}"`;
}

function normalizeSourceBinding(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const sourceText = String(value.sourceText ?? "");
  return sourceText
    ? {
        sourceText,
        sourceStart: Number.parseInt(value.sourceStart, 10),
        sourceEnd: Number.parseInt(value.sourceEnd, 10),
        sourceImplicit: value.implicit === true || value.sourceImplicit === true,
      }
    : null;
}

function normalizeExpressionNode(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const type = String(value.type || "").trim().toLowerCase();
  if (type === "term") {
    const source = normalizeSourceBinding(value);
    return {
      type: "term",
      key: String(value.key || value.label || "").trim(),
      path: String(value.path || "").trim(),
      label: String(value.label || "").trim(),
      kindLabel: String(value.kindLabel || "").trim(),
      description: String(value.description || "").trim(),
      contentMarkup: String(value.contentMarkup || "").trim(),
      grade: String(value.grade || "").trim(),
      negated: normalizeNegated(value.negated ?? value.not ?? value.inverted),
      reserveSecondaryRow: value.reserveSecondaryRow === true,
      ...(source || {}),
    };
  }

  if (type === "container") {
    const child = normalizeExpressionNode(
      value.expression ?? (Array.isArray(value.children) ? value.children[0] : null),
    );
    if (!child) {
      return null;
    }
    return {
      type: "container",
      key: String(value.key || value.path || "container").trim(),
      path: String(value.path || "").trim(),
      negated: normalizeNegated(value.negated ?? value.not ?? value.inverted),
      children: [child],
    };
  }

  if (type !== "group") {
    return null;
  }

  const source = normalizeSourceBinding(value);
  return {
    type: "group",
    key: String(value.key || value.path || "group").trim(),
    path: String(value.path || "").trim(),
    label: String(value.label || "").trim(),
    kindLabel: String(value.kindLabel || "").trim(),
    description: String(value.description || "").trim(),
    contentMarkup: String(value.contentMarkup || "").trim(),
    grade: String(value.grade || "").trim(),
    operator: normalizeOperator(value.operator),
    negated: normalizeNegated(value.negated ?? value.not ?? value.inverted),
    ...(source || {}),
    operatorSources: (Array.isArray(value.operatorSources) ? value.operatorSources : [])
      .map((binding) => normalizeSourceBinding(binding)),
    children: (Array.isArray(value.children) ? value.children : [])
      .map((child) => normalizeExpressionNode(child))
      .filter((child) => child && !(child.type === "group" && child.children.length === 0)),
  };
}

function buildRenderKey(node) {
  if (!node) {
    return null;
  }
  if (node.type === "term") {
    return [
      "term",
      node.key,
      node.path,
      node.label,
      node.kindLabel,
      node.description,
      node.contentMarkup,
      node.grade,
      node.negated,
      node.reserveSecondaryRow,
      node.sourceText,
      node.sourceStart,
      node.sourceEnd,
      node.sourceImplicit,
    ];
  }
  if (node.type === "container") {
    return [
      "container",
      node.key,
      node.path,
      node.negated,
      node.children.map((child) => buildRenderKey(child)),
    ];
  }
  return [
    "group",
    node.key,
    node.path,
    node.label,
    node.kindLabel,
    node.description,
    node.contentMarkup,
    node.grade,
    node.operator,
    node.negated,
    node.sourceText,
    node.sourceStart,
    node.sourceEnd,
    node.sourceImplicit,
    node.operatorSources,
    node.children.map((child) => buildRenderKey(child)),
  ];
}

function semanticNodeAccessibleLabel(node, meaningCopy) {
  if (!node || typeof node !== "object") {
    return meaningCopy.appliedQuery;
  }
  if (node.type === "term") {
    return [
      node.negated ? meaningCopy.exclude : "",
      node.kindLabel,
      node.label || meaningCopy.appliedTerm,
      node.description,
    ].filter(Boolean).join(": ");
  }
  if (node.type === "container") {
    const child = node.children[0];
    return [
      node.negated ? meaningCopy.exclude : "",
      meaningCopy.containerContaining,
      semanticNodeAccessibleLabel(child, meaningCopy),
    ].filter(Boolean).join(": ");
  }
  const operator = normalizeOperator(node.operator);
  const matchMeaning = operator === "and" ? meaningCopy.matchAll : meaningCopy.matchAny;
  const conjunction = operator === "and"
    ? meaningCopy.conjunctionAnd
    : meaningCopy.conjunctionOr;
  const children = node.children.map((child) => semanticNodeAccessibleLabel(child, meaningCopy));
  if (children.length === 1) {
    return [
      node.negated ? meaningCopy.exclude : "",
      children[0],
    ].filter(Boolean).join(": ");
  }
  return [
    node.negated ? meaningCopy.exclude : "",
    matchMeaning,
    children.join(` ${conjunction} `),
  ].filter(Boolean).join(": ");
}

function expressionReservesSecondaryRow(node) {
  if (!node || typeof node !== "object") {
    return false;
  }
  if (node.type === "term") {
    return node.reserveSecondaryRow === true;
  }
  return (Array.isArray(node.children) ? node.children : [])
    .some((child) => expressionReservesSecondaryRow(child));
}

function sourceRange(node) {
  const start = Number.parseInt(node?.sourceStart, 10);
  const end = Number.parseInt(node?.sourceEnd, 10);
  const sourceText = String(node?.sourceText ?? "");
  if (
    !sourceText ||
    !Number.isInteger(start) ||
    !Number.isInteger(end) ||
    start < 0 ||
    end <= start ||
    end - start !== sourceText.length
  ) {
    return null;
  }
  return {
    start,
    end,
    sourceText,
    implicit: node.sourceImplicit === true,
  };
}

function renderTermEffect(node, escapeHtml, meaningCopy) {
  const label = node.label || meaningCopy.appliedTerm;
  return `
    <div class="fishy-applied-term-content">
      ${node.contentMarkup || `<span class="fishy-applied-cue-label">${escapeHtml(label)}</span>`}
    </div>
  `;
}

function renderRangeConnector() {
  return `
    <svg
      class="fishy-applied-query-connector"
      data-query-connector
      viewBox="0 0 1 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M 0.5 0 V 100"></path>
    </svg>
  `;
}

function unitEnvelope(units) {
  if (!Array.isArray(units) || units.length === 0) {
    return null;
  }
  return {
    start: units.reduce((start, unit) => Math.min(start, unit.start), units[0].start),
    end: units.reduce((end, unit) => Math.max(end, unit.end), units[0].end),
  };
}

function collapsedSourceProjection(range, node) {
  return range
    ? {
        complete: true,
        units: [{ ...range, kind: "group", node }],
        groups: [],
        envelope: { start: range.start, end: range.end },
      }
    : { complete: false, units: [], groups: [], envelope: null };
}

// Keep every operator binding in the validated projection. Presentation may
// omit an OR unit, while AND groups retain exact source spans when precedence
// would otherwise be implicit to a reader.
function collectSourceUnits(node, parentOperator = null) {
  const range = sourceRange(node);
  if (node.type === "term") {
    return {
      complete: Boolean(range),
      units: range ? [{ ...range, kind: "term", node }] : [],
      groups: [],
      envelope: range ? { start: range.start, end: range.end } : null,
    };
  }
  if (node.type === "container") {
    const child = node.children[0];
    return child
      ? collectSourceUnits(child, parentOperator)
      : collapsedSourceProjection(range, node);
  }

  const units = [];
  const groups = [];
  const childProjections = [];
  for (let index = 0; index < node.children.length; index += 1) {
    const childProjection = collectSourceUnits(
      node.children[index],
      normalizeOperator(node.operator),
    );
    if (!childProjection.complete) {
      return collapsedSourceProjection(range, node);
    }
    units.push(...childProjection.units);
    groups.push(...childProjection.groups);
    childProjections.push(childProjection);
    if (index === 0) {
      continue;
    }
    const operatorRange = sourceRange(node.operatorSources[index - 1]);
    if (!operatorRange) {
      return collapsedSourceProjection(range, node);
    }
    units.push({
      ...operatorRange,
      kind: "operator",
      operator: normalizeOperator(node.operator),
      path: `${node.path}::operator:${index}`,
    });
  }
  if (units.length) {
    const envelope = unitEnvelope(units);
    const operator = normalizeOperator(node.operator);
    if (
      operator === "and" &&
      parentOperator === "or" &&
      childProjections.length > 1 &&
      childProjections[0].envelope &&
      childProjections.at(-1).envelope
    ) {
      groups.push({
        path: node.path,
        start: childProjections[0].envelope.start,
        end: childProjections.at(-1).envelope.end,
      });
    }
    return { complete: true, units, groups, envelope };
  }
  return collapsedSourceProjection(range, node);
}

function orderedSourceUnits(rootNode, sourceText) {
  const projection = collectSourceUnits(rootNode);
  if (!projection.complete || projection.units.length === 0) {
    return null;
  }
  const units = projection.units.slice().sort((left, right) =>
    left.start - right.start || left.end - right.end
  );
  let previousEnd = -1;
  for (const unit of units) {
    if (
      unit.start < previousEnd ||
      unit.end > sourceText.length ||
      sourceText.slice(unit.start, unit.end) !== unit.sourceText
    ) {
      return null;
    }
    previousEnd = unit.end;
  }
  return { units, groups: projection.groups };
}

function buildSourceLayout(sourceText, projection) {
  const units = projection.units;
  const groups = projection.groups;
  const boundaries = Array.from(new Set([
    0,
    sourceText.length,
    ...units.flatMap((unit) => [unit.start, unit.end]),
    ...groups.flatMap((group) => [group.start, group.end]),
  ])).sort((left, right) => left - right);
  const boundaryColumns = new Map(
    boundaries.map((boundary, index) => [boundary, index + 1]),
  );
  const segments = boundaries.slice(0, -1).map((start, index) => {
    const end = boundaries[index + 1];
    return {
      start,
      end,
      sourceText: sourceText.slice(start, end),
      column: index + 1,
    };
  });
  const laidOutUnits = units.map((unit) => ({
    ...unit,
    startColumn: boundaryColumns.get(unit.start),
    endColumn: boundaryColumns.get(unit.end),
    lane: 2,
  }));
  const laidOutGroups = groups.map((group) => ({
    ...group,
    startColumn: boundaryColumns.get(group.start),
    endColumn: boundaryColumns.get(group.end),
  }));
  if (
    segments.some((segment) => !segment.sourceText || segment.end <= segment.start) ||
    laidOutUnits.some((unit) =>
      !Number.isInteger(unit.startColumn) ||
      !Number.isInteger(unit.endColumn) ||
      unit.endColumn <= unit.startColumn
    ) ||
    laidOutGroups.some((group) =>
      !Number.isInteger(group.startColumn) ||
      !Number.isInteger(group.endColumn) ||
      group.endColumn <= group.startColumn
    )
  ) {
    return null;
  }
  return { segments, units: laidOutUnits, groups: laidOutGroups };
}

function renderSourceMeasurementSegments(segments, escapeHtml) {
  return segments.map((segment) => `
    <span
      class="fishy-applied-query-measure-segment"
      aria-hidden="true"
      data-query-measure-start="${escapeHtml(segment.start)}"
      data-query-measure-end="${escapeHtml(segment.end)}"
      style="grid-column: ${segment.column}; grid-row: 1;"
    >${escapeHtml(segment.sourceText)}</span>
  `).join("");
}

function renderSourceUnit(unit, escapeHtml, meaningCopy, queryTextRelation) {
  const rangeAttributes = ` data-query-source-start="${escapeHtml(unit.start)}" data-query-source-end="${escapeHtml(unit.end)}"`;
  const implicitAttribute = unit.implicit ? ' data-query-source-implicit="true"' : "";
  const relationAttribute = queryTextRelationAttribute(queryTextRelation, escapeHtml);
  const layoutAttributes = ` data-query-effect-lane="${escapeHtml(unit.lane - 1)}" style="grid-column: ${unit.startColumn} / ${unit.endColumn}; grid-row: ${unit.lane};"`;

  if (unit.kind === "operator") {
    if (unit.operator !== "and") {
      return "";
    }
    const conjunction = meaningCopy.conjunctionAnd;
    const matchMeaning = meaningCopy.matchAll;
    const accessibleLabel = `${conjunction}: ${matchMeaning}`;
    return `
      <div
        class="fishy-applied-query-unit fishy-applied-and-unit"
        role="listitem"
        aria-label="${escapeHtml(accessibleLabel)}"
        data-query-source-unit-kind="operator"
        data-query-has-cue="true"
        data-expression-operator="${escapeHtml(unit.operator)}"
        data-expression-path="${escapeHtml(unit.path)}"${rangeAttributes}${implicitAttribute}${relationAttribute}
        ${layoutAttributes}
      >
        <span class="fishy-applied-query-range-anchor" aria-hidden="true"></span>
        <div class="fishy-applied-query-unit-effect" data-query-cue aria-hidden="true">
          <span class="fishy-applied-and-hub">&amp;</span>
        </div>
        ${renderRangeConnector()}
      </div>
    `;
  }

  const node = unit.node;
  const label = node.label || meaningCopy.appliedTerm;
  const hasCue = node.type === "term" || Boolean(node.contentMarkup || node.label);
  const accessibleLabel = semanticNodeAccessibleLabel(node, meaningCopy);
  return `
    <div
      class="fishy-applied-query-unit fishy-applied-term${node.type === "group" ? " fishy-applied-source-group" : ""}"
      role="listitem"
      aria-label="${escapeHtml(accessibleLabel)}"
      data-query-source-unit-kind="${unit.kind}"
      data-expression-node-kind="${escapeHtml(node.type)}"
      data-expression-path="${escapeHtml(node.path)}"${
        node.type === "term" ? ` data-expression-key="${escapeHtml(node.key || label)}"` : ""
      }${node.grade ? ` data-grade="${escapeHtml(node.grade)}"` : ""}${
        node.negated ? ' data-expression-negated="true"' : ""
      } data-query-has-cue="${hasCue}"${rangeAttributes}${relationAttribute}
      ${layoutAttributes}
    >
      <span class="fishy-applied-query-range-anchor" aria-hidden="true"></span>
      ${hasCue ? `<div class="fishy-applied-query-unit-effect" data-query-cue>${renderTermEffect(node, escapeHtml, meaningCopy)}</div>${renderRangeConnector()}` : ""}
    </div>
  `;
}

function renderAndGroupCue(group, escapeHtml, queryTextRelation) {
  const relationAttribute = queryTextRelationAttribute(queryTextRelation, escapeHtml);
  return `
    <span
      class="fishy-applied-and-group-cue"
      aria-hidden="true"
      data-query-and-group="true"
      data-expression-path="${escapeHtml(group.path)}"
      data-expression-operator="and"
      data-query-group-start="${escapeHtml(group.start)}"
      data-query-group-end="${escapeHtml(group.end)}"${relationAttribute}
      style="grid-column: ${group.startColumn} / ${group.endColumn}; grid-row: 2;"
    ></span>
  `;
}

function sourceRangeProjection(unit) {
  return {
    start: unit.start,
    end: unit.end,
    sourceText: unit.sourceText,
    kind: unit.kind,
    implicit: unit.implicit === true,
    path: unit.kind === "operator" ? unit.path : unit.node.path,
  };
}

export function buildAppliedSearchTermsView(expression, options = {}) {
  const escapeHtml =
    typeof options.escapeHtml === "function" ? options.escapeHtml : defaultEscapeHtml;
  const normalizedExpression = normalizeExpressionNode(expression);
  const meaningCopy = normalizeMeaningCopy(options.meaningCopy);
  const rootNode =
    normalizedExpression?.type === "group"
      ? normalizedExpression
      : normalizedExpression
        ? {
            type: "group",
            key: "root",
            path: "root",
            label: "",
            description: "",
            operator: "or",
            sourceText: "",
            sourceStart: Number.NaN,
            sourceEnd: Number.NaN,
            operatorSources: [],
            children: [normalizedExpression],
          }
        : null;
  const sourceText = String(options.sourceText ?? "");
  const renderTreeKey = buildRenderKey(rootNode);
  const queryTextRelation = normalizeQueryTextRelation(options.queryTextRelation);
  const aggregateLabel = rootNode
    ? semanticNodeAccessibleLabel(rootNode, meaningCopy)
    : meaningCopy.appliedQuery;
  const renderKey = rootNode
    ? JSON.stringify([queryTextRelation, sourceText, meaningCopy, renderTreeKey])
    : JSON.stringify(renderTreeKey);
  const sourceUnits = rootNode && sourceText
    ? orderedSourceUnits(rootNode, sourceText)
    : null;
  const sourceLayout = sourceUnits ? buildSourceLayout(sourceText, sourceUnits) : null;
  const reserveSecondaryRow = expressionReservesSecondaryRow(rootNode);

  if (!rootNode || !sourceLayout || queryTextRelation === "prior-query") {
    return {
      hasContent: false,
      html: "",
      renderKey,
      sourceRanges: [],
    };
  }

  const html = `
    <section
      class="fishy-applied-expression fishy-applied-query-rail${reserveSecondaryRow ? " pb-9" : ""}"
      role="list"
      aria-label="${escapeHtml(`${meaningCopy.appliedQuery}: ${aggregateLabel}`)}"
      data-expression-node-kind="group"
      data-expression-path="${escapeHtml(rootNode.path || "root")}"
      data-expression-operator="${escapeHtml(rootNode.operator)}"
      data-query-source-length="${escapeHtml(sourceText.length)}"
      data-query-source-segments="${escapeHtml(sourceLayout.segments.length)}"
      ${reserveSecondaryRow ? 'data-query-secondary-row="true"' : ""}
      style="--fishymap-query-source-segments: ${Math.max(1, sourceLayout.segments.length)};"
    >
      ${renderSourceMeasurementSegments(sourceLayout.segments, escapeHtml)}
      ${sourceLayout.groups.map((group) => renderAndGroupCue(group, escapeHtml, queryTextRelation)).join("")}
      ${sourceLayout.units.map((unit) => renderSourceUnit(unit, escapeHtml, meaningCopy, queryTextRelation)).join("")}
    </section>
  `;

  return {
    hasContent: true,
    html,
    renderKey,
    sourceRanges: sourceLayout.units.map((unit) => sourceRangeProjection(unit)),
  };
}
